import React, { useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import {
  isSameDay,
  isAfter,
  isBefore,
  isToday,
  isSunday,
  isSaturday,
  isLastDayOfMonth,
  isFirstDayOfMonth,
  addDays
} from 'date-fns';

import { DayContainer } from './styles';
import { useDay } from '@spa/shared/hooks/dateRange';
import Tooltip from '@spa/shared/components/Tooltip';
import { formatDate } from '@spa/shared/utils';
import { CalendarContext } from '@spa/shared/contexts/calendar';

const Day = ({ dayLabel, date, dateRange = {} }) => {
  const dayRef = useRef();
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const { validateDates } = useContext(CalendarContext);
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    startDate,
    endDate,
    blockedDates = [],
    minBookingDays,
    maxMoveInDate,
    choosingStart,
    choosingEnd
  } = dateRange;
  const { disabledDate, onClick } = useDay(dayRef, date, dateRange);

  const isBetween = isAfter(date, startDate) && isBefore(date, endDate);
  const isFirstOfMonth = isFirstDayOfMonth(date);
  const isLastOfMonth = isLastDayOfMonth(date);
  const isSundayDay = isSunday(date);
  const isSaturdayDay = isSaturday(date);
  const isBlocked = !!blockedDates.find(blockedDate => isSameDay(date, blockedDate));
  const isMinimumStay = startDate && isAfter(date, startDate) && isBefore(date, addDays(startDate, minBookingDays));
  const isMinimumStayLast = startDate && isSameDay(date, addDays(startDate, minBookingDays));
  const isMaxMoveInExceeded = !startDate && maxMoveInDate && isAfter(date, maxMoveInDate);
  const isDisabled = disabledDate || isBlocked || !date;

  const dayClasses = {
    disabled: isDisabled,
    'available-move-in': !isDisabled && choosingStart && maxMoveInDate && !isMaxMoveInExceeded,
    'selected-start': isSameDay(date, startDate),
    'selected-end': isSameDay(date, endDate),
    selected: isSameDay(date, startDate) || isSameDay(date, endDate),
    'minimum-stay': isAfter(date, startDate) && isBefore(date, addDays(startDate, minBookingDays)),
    'minimum-stay-last': isSameDay(date, addDays(startDate, minBookingDays)),
    'selected-between': isBetween,
    first: isSundayDay || isFirstOfMonth,
    last: isSaturdayDay || isLastOfMonth,
    only: (isFirstOfMonth && isSaturdayDay) || (isLastOfMonth && isSundayDay),
    today: date && isToday(date),
    'no-day': !date,
    'max-move-in': isMaxMoveInExceeded
  };

  const handleOnClick = useCallback(
    event => {
      onClick(event);

      if (choosingEnd && startDate && date && isMinimumStay) {
        validateDates(formatDate(new Date(startDate)), formatDate(date));
      }
    },
    [choosingEnd, startDate, date, isMinimumStay, onClick, validateDates]
  );

  const handleTouchStart = useCallback(() => {
    if (dayClasses['max-move-in'] || dayClasses['minimum-stay'] || dayClasses['minimum-stay-last']) {
      setShowTooltip(true);
    }
  }, [dayClasses, setShowTooltip]);

  const getTooltipContent = () => {
    if (isMaxMoveInExceeded) {
      return t('Move-in must be before {{date}}', { date: formatDate(maxMoveInDate, language, 'MMM dd') });
    }

    if (isMinimumStay || isMinimumStayLast) {
      return t('Minimum of {{nights}} nights', { nights: minBookingDays });
    }

    return null;
  };

  const className = Object.keys(dayClasses)
    .filter(name => dayClasses[name])
    .join(' ');

  const tooltipContent = getTooltipContent();
  const shouldShowTooltip = !!tooltipContent && showTooltip;

  return (
    <DayContainer
      ref={dayRef}
      tabIndex={dayClasses.disabled ? null : '-1'}
      className={className}
      onClick={handleOnClick}
      onTouchStart={handleTouchStart}
      choosingStart={choosingStart}
    >
      {shouldShowTooltip ? (
        <Tooltip bottom={false}>
          {dayLabel}
          <span>{tooltipContent}</span>
        </Tooltip>
      ) : (
        dayLabel
      )}
    </DayContainer>
  );
};

Day.propTypes = {
  dayLabel: PropTypes.string,
  date: PropTypes.instanceOf(Date),
  dateRange: PropTypes.object
};

export default Day;
